import type { AgentActivityItem } from '../../components/ui/beui';
import type { AIGenerationProgressEvent } from '../../types';

/** Phrases cycled while the model is still thinking (no pipeline events yet). */
export const IDLE_PHRASES = [
  'Reading your prompt',
  'Choosing technologies',
  'Sketching the architecture',
  'Wiring up the connections',
];

/** Map pipeline progress events onto AgentActivity step items. */
export function buildActivityItems(
  events: AIGenerationProgressEvent[],
  providerName: string,
  hasExisting: boolean,
): { items: AgentActivityItem[]; done: { score: number; repairRounds: number } | null } {
  const items: AgentActivityItem[] = [];
  let done: { score: number; repairRounds: number } | null = null;

  const settle = (id: string, meta?: string) => {
    const item = items.find((i) => i.id === id);
    if (item && item.type === 'step') {
      item.status = 'complete';
      if (meta) item.meta = meta;
    }
  };

  for (const event of events) {
    switch (event.type) {
      case 'start':
        items.push({
          id: 'generate',
          type: 'step',
          label: hasExisting
            ? `Revising your diagram with ${providerName}`
            : `Generating stack with ${providerName}`,
          status: 'active',
        });
        break;
      case 'generated':
        settle('generate');
        break;
      case 'validating':
        items.push({ id: 'validate', type: 'step', label: 'Validating layout geometry', status: 'active' });
        break;
      case 'validated':
        settle(
          'validate',
          `${event.score}/100${event.issueCount > 0 ? ` · ${event.issueCount} issue${event.issueCount === 1 ? '' : 's'}` : ' · clean'}`,
        );
        break;
      case 'repairing':
        items.push({
          id: `repair-${event.round}`,
          type: 'step',
          label: `Repairing layout — round ${event.round}`,
          status: 'active',
          meta: `${event.score}/100`,
        });
        break;
      case 'repaired':
        settle(`repair-${event.round}`, `→ ${event.score}/100`);
        break;
      case 'done':
        done = { score: event.score, repairRounds: event.repairRounds };
        break;
    }
  }
  return { items, done };
}
